import React, { FC, Ref, useRef, useState } from 'react'
import { Popup } from '../catalog/Popup'
import styled from 'styled-components'
import SwapInput from './SwapInput'
import ValidationButton from '../catalog/ValidationButton'
import { swaapRed0 } from '../Themes'
import { EmptyLine } from '../catalog/EmptyLine'
import { riskySlippageThreshold } from '../../config'
import { isNumericString, isSimpleNumericString } from '../../utils/num'

interface Props {
  onExit: VoidFunction
  onSlippageChange: (slippage: number) => void
  // slippage from localStorage. It's a 0.003 to represent '0.3%'
  currentSlippage: number
  context: 'lp' | 'trade'
}

export function slippageIsRisky(value: number): boolean {
  return value < riskySlippageThreshold
}

/**
 * Trick to avoid 0.0001*100 = 0.009999999999999
 * @param slippage that is NOT a percentage
 */
export function roundSlippageForPercentDisplay(slippage: number): number {
  return Number((slippage * 100).toFixed(4))
}

export const Settings: FC<Props> = (props: Props) => {
  const { onExit, onSlippageChange, currentSlippage } = props
  const inputRef = useRef<HTMLInputElement>(null)

  const [valid, setValid] = useState(false)
  const [percentSlippage, setPercentSlippage] = useState(
    roundSlippageForPercentDisplay(currentSlippage)
  )

  // Slippage candidate is changed when typing change, but is not saved
  const [slippageCandidate, setSlippageCandidate] = useState(currentSlippage)

  const ref = useRef()

  return (
    <PopupWrapper>
      <Popup outsideRef={ref} onExit={onExit} title={'Settings'}>
        <SlippageSection>
          <div className={'justify-between'}>
            <div className={'big-text'} style={{ alignSelf: 'center' }}>
              Slippage tolerance
            </div>
            <div>
              <SwapInput
                right={true}
                ref={inputRef}
                placeholder={percentSlippage.toString()}
                defaultValue={''}
                onChange={() => {
                  if (!inputRef.current) {
                    setValid(false)
                    return
                  }
                  const userText = toValidSlippage(inputRef.current.value)
                  if (userText) {
                    setValid(true)
                    setSlippageCandidate(userText / 100)
                    setValid(true)
                    setPercentSlippage(userText)
                  } else {
                    setValid(false)
                  }
                }}
              />
              <span className={'big-text'}>%</span>
            </div>
          </div>
          {slippageIsRisky(slippageCandidate) && (
            <SlippageIsRisky className={'light'}>
              Your transaction may fail
            </SlippageIsRisky>
          )}
        </SlippageSection>
        <EmptyLine />
        <ValidationButton
          isReady={valid}
          onClick={() => {
            if (!valid) {
              return
            }

            if (
              // it should be already valid, but we make a double check for js resistance
              inputRef.current &&
              isSimpleNumericString(inputRef.current.value)
            ) {
              const percentSlippage = Number(inputRef.current.value)
              const userSlippage = percentSlippage / 100
              window.localStorage.setItem('slippage', userSlippage.toString())
              onSlippageChange(userSlippage)
            }
            onExit()
          }}
        >
          {'Change tolerance'}
        </ValidationButton>
      </Popup>
    </PopupWrapper>
  )
}

function toValidSlippage(slippageString: string): number | undefined {
  if (!slippageString) {
    return
  }
  return isSimpleNumericString(slippageString)
    ? Number(slippageString)
    : undefined
}

// TODO: include this in the Popup component
const PopupWrapper = styled.div`
  font-size: 0.85em;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  justify-content: center;
`

const SlippageSection = styled.div`
  display: flex;
  flex-direction: column;
`

const SlippageIsRisky = styled.div`
  font-size: 1.2rem;
  color: ${swaapRed0};
  text-align: left;
`
